import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import { CheckCircle2, XCircle, ShieldPlus, FileText } from "lucide-react";
import { STATUS_COLOR } from "./_helpers";
import RowDetailSheet from "@/components/RowDetailSheet";

export default function DiscountApprovalsTab() {
  const [rows, setRows] = useState([]);
  const [mine, setMine] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [quotations, setQuotations] = useState([]);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ quotation_id: "", category_key: "", requested_pct: 2.5, document_ref: "", remarks: "" });

  const load = async () => {
    const [a, m, c, q] = await Promise.all([
      api.get("/dealership/discount-approvals"),
      api.get("/dealership/discount-approvals/pending-for-me"),
      api.get("/dealership/config"),
      api.get("/dealership/quotations", { params: { status: "draft" } }),
    ]);
    setRows(a.data); setMine(m.data.approvals || []); setCfg(c.data); setQuotations(q.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.quotation_id || !form.category_key) return toast.error("Quotation and category required");
    try { await api.post("/dealership/discount-approvals", form); toast.success("Approval request created"); setOpen(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const act = async (a, action) => {
    try { await api.post(`/dealership/discount-approvals/${a.id}/${action}`); toast.success(`${action}d`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const catByKey = (k) => (cfg?.discount_categories || []).find((c) => c.key === k);

  return (
    <div className="space-y-4" data-testid="discount-approvals-tab">
      {mine.length > 0 && (
        <div className="card-sharp p-4 border-amber-500/40 bg-amber-500/5" data-testid="da-inbox">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-amber-600 mb-3">Awaiting your approval · {mine.length}</div>
          <div className="space-y-2">
            {mine.map((a) => (
              <div key={a.id} className="flex items-center justify-between gap-3 border rounded-[2px] p-2.5" data-testid={`da-inbox-${a.approval_number}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-xs"><span className="font-mono">{a.approval_number}</span> · {a.category_label} @ <span className="font-mono">{a.requested_pct}%</span></div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">Doc: {a.document_ref || "—"} · Required: {a.required_doc || "—"}</div>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" className="h-7 text-xs text-emerald-600 gap-1" onClick={() => act(a, "approve")} data-testid={`da-approve-${a.approval_number}`}><CheckCircle2 className="w-3.5 h-3.5" /> Approve</Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs text-red-500 gap-1" onClick={() => act(a, "reject")}><XCircle className="w-3.5 h-3.5" /> Reject</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <Button onClick={() => setOpen(true)} className="rounded-[2px] bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="new-da-btn"><ShieldPlus className="w-3.5 h-3.5" /> Request Discount Approval</Button>
      </div>

      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>DA#</th><th>Quotation</th><th>Category</th><th className="text-right">%</th><th>Required Doc</th><th>Approver</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="8" className="text-center py-10 text-muted-foreground">No approval requests yet.</td></tr>}
            {rows.map((a) => (
              <tr key={a.id} data-testid={`da-row-${a.approval_number}`}
                    className="cursor-pointer hover:bg-muted/40 transition-colors"
                    onClick={() => setDetail(a)}>
                <td className="font-mono text-xs">{a.approval_number}</td>
                <td className="font-mono text-[10px] text-muted-foreground">{a.quotation_id.slice(0, 8)}</td>
                <td className="text-xs">{a.category_label}</td>
                <td className="text-right tabular-nums text-xs">{a.requested_pct}%</td>
                <td className="text-[10px] text-muted-foreground">{a.required_doc || "—"}<div className="text-[10px] font-mono text-emerald-600">{a.document_ref || ""}</div></td>
                <td className="text-[10px] font-mono uppercase">{a.approver_role}</td>
                <td><span className={`text-[10px] font-mono uppercase ${STATUS_COLOR[a.status] || ""}`}>{a.status}</span></td>
                <td className="text-[10px] font-mono text-muted-foreground">{fmtDate(a.requested_at?.slice(0, 10))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-lg rounded-none space-y-3 overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-heading">Request Discount Approval</SheetTitle>
            <SheetDescription className="sr-only">Discounts above policy cap require role-based approval with supporting document.</SheetDescription>
          </SheetHeader>
          <div className="space-y-3">
            <div>
              <Label>Quotation *</Label>
              <Select value={form.quotation_id} onValueChange={(v) => setForm({ ...form, quotation_id: v })}>
                <SelectTrigger className="rounded-[2px] mt-1" data-testid="da-quote"><SelectValue placeholder="Pick draft quotation" /></SelectTrigger>
                <SelectContent>{quotations.map((q) => <SelectItem key={q.id} value={q.id}>{q.quotation_number} · {q.customer?.first_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Discount category *</Label>
              <Select value={form.category_key} onValueChange={(v) => setForm({ ...form, category_key: v })}>
                <SelectTrigger className="rounded-[2px] mt-1" data-testid="da-category"><SelectValue placeholder="Pick category" /></SelectTrigger>
                <SelectContent>
                  {(cfg?.discount_categories || []).map((c) => <SelectItem key={c.key} value={c.key}>{c.label} · cap {c.max_pct}% · approver {c.approver_role}</SelectItem>)}
                </SelectContent>
              </Select>
              {form.category_key && catByKey(form.category_key) && (
                <div className="text-[10px] text-muted-foreground mt-1 font-mono">Required doc: {catByKey(form.category_key).required_doc}</div>
              )}
            </div>
            <div><Label>Requested % (max {form.category_key ? catByKey(form.category_key)?.max_pct : "—"})</Label>
              <Input type="number" step="0.1" value={form.requested_pct} onChange={(e) => setForm({ ...form, requested_pct: Number(e.target.value) })} className="rounded-[2px] mt-1 font-mono" data-testid="da-pct" />
            </div>
            <div><Label>Document reference / URL</Label>
              <Input value={form.document_ref} onChange={(e) => setForm({ ...form, document_ref: e.target.value })} className="rounded-[2px] mt-1 font-mono" placeholder="Attachment id / drive link" data-testid="da-doc" />
            </div>
            <div><Label>Remarks</Label>
              <Textarea value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} className="rounded-[2px] mt-1" rows={3} />
            </div>
            <Button onClick={save} className="w-full rounded-[2px] bg-accent hover:bg-accent/90 text-white" data-testid="da-save">Submit request</Button>
          </div>
        </SheetContent>
      </Sheet>
      <RowDetailSheet row={detail} onClose={() => setDetail(null)} title="Discount approval" testId="da-detail" />
    </div>
  );
}
